import React, { useEffect, useState, forwardRef,useRef,useCallback} from "react";  
import axios from 'axios';  
import {useTranslation} from "react-i18next";
import { motion } from "framer-motion";
import { useNavigate } from 'react-router-dom';
import { CREDIT_API_URL, ASSETS_URL } from "../../constants";
import useChatStore from '../../store/useChatStore';
import { Doughnut } from 'react-chartjs-2';  
import {  
    Chart as ChartJS,  
    ArcElement,  
    Tooltip,  
    Legend  
} from 'chart.js';  
import { formatToIndianRupee } from "../../utility/FormatToIndianRupee";
import OtpVerificationModal from '../../utility/OtpVerificationModal';
import { fadeInUp, fadeInLeft, fadeInRight, buttonVariants } from "../../utility/framerMotionAnimations";
import { customerSignIn } from '../../services/leadService';
import showToast from '../../utility/showToast';
import useUtmStore from '../../store/useUtmStore';
ChartJS.register(ArcElement, Tooltip, Legend);  



const CommercialVehicleEmiSection = forwardRef(({ selectedValue ,productType}, ref) => {  

     {/*Calculator Section */}
     const {t} =useTranslation(['commercialVehicleLoanPage','homepage']);
     const [loanFactorApiResponse, setLoanFactorApiResponse] = useState({}); 
     const [loanAmount, setLoanAmount] = useState(0);  
     const [interestRate, setInterestRate] = useState(0);  
     const [loanTenure, setLoanTenure] = useState(0);  
     const [apiResponse, setApiResponse] = useState({});  
     const [isApiCallInProgress, setIsApiCallInProgress] = useState(false);
     const [isMobileValid, setIsMobileValid] = useState(false);
     const debounceTimeout = useRef(null);
     const [interestRateText, setInterestRateText] = useState(0)
     const [loanAmountText, setLoanAmountText] = useState(0);
     const [loanTenureText, setLoanTenureText] = useState(0);
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [leadData, setLeadData] = useState(null);
    const utm = useUtmStore(state => state.utm || {});
     {/*Fetching Loan Factor */}
    const fetchLoanFactor = useCallback(async () => {
        try {
            const payload = { productType: productType };
            const response = await axios.post(`${CREDIT_API_URL}fetch-loan-factor`, payload);
            setLoanFactorApiResponse(response.data.data);
            setLoanAmount(response.data.data.minLoanAmount);
            setInterestRate(response.data.data.minROI);
            setLoanTenure(response.data.data.minTenure);
            setLoanAmountText(formatToIndianRupee(data.minLoanAmount));  // Initialize
                setInterestRateText(`${data.minROI}%`);        // Initialize
                setLoanTenureText(`${data.minTenure} months`);      // Initialize
        } catch (error) {
            console.error("Error fetching loan factor:", error.response ? error.response.data : error.message);
        }
    }, [productType]);

    useEffect(() => {
        fetchLoanFactor();
    }, [fetchLoanFactor]);

    // Debounced function to call the loan data API
    const fetchLoanData = useCallback(async () => {
        if (isApiCallInProgress) return; // Prevent making multiple API calls simultaneously

        setIsApiCallInProgress(true); // Mark API call as in progress
        try {
            const payload = {
                principal: loanAmount,
                annualRate: interestRate,
                tenure: loanTenure,
            };
            const response = await axios.post(`${CREDIT_API_URL}calculate-emi`, payload);
            setApiResponse(response.data.data);
        } catch (error) {
            console.error("Error fetching loan data:", error.response ? error.response.data : error.message);
        } finally {
            setIsApiCallInProgress(false); // Mark API call as completed
        }
    }, [loanAmount, interestRate, loanTenure, isApiCallInProgress]);

    // Debounced state change handlers
    const handleLoanAmountChange = (e) => {
        const newAmount = parseFloat(e.target.value);
        setLoanAmount(newAmount);
        setLoanAmountText(`${formatToIndianRupee(newAmount)}`);
        debounceApiCall(); // Trigger debounce for API call
    };

    const handleInterestRateChange = (e) => {
        const newRate = parseFloat(e.target.value);
        setInterestRate(newRate);
        setInterestRateText(`${newRate}%`);
        debounceApiCall(); // Trigger debounce for API call
    };

    const handleLoanTenureChange = (e) => {
        const newTenure = Number(e.target.value);
        setLoanTenure(newTenure);
        setLoanTenureText(`${newTenure} months`);
        debounceApiCall(); // Trigger debounce for API call
    };

    // Debounced function to call the fetchLoanData after delay
    const debounceApiCall = useCallback(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current); 
        }

        debounceTimeout.current = setTimeout(() => {
            fetchLoanData(); 
        }, 500);
    }, [fetchLoanData]);

   // Only trigger loan data fetch once after loanFactorApiResponse is available
    useEffect(() => {
        
            fetchLoanData();
        
    }, [loanFactorApiResponse]);

    const handleLoanAmountInput = (e) => {
        const value = e.target.innerText.trim().replace(/[₹,]/g, '').trim();
        const parsedValue = parseInt(value);

        // Save the current selection (cursor position)
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);

        // Clear the previous timeout if it exists
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        // Set a new timeout to call the function after a delay
        debounceTimeout.current = setTimeout(() => {
            if (!isNaN(parsedValue) && parsedValue >= loanFactorApiResponse.minLoanAmount && parsedValue <= loanFactorApiResponse.maxLoanAmount) {
                setLoanAmount(parsedValue);
                setLoanAmountText(formatToIndianRupee(parsedValue)); // Update the formatted text
                debounceApiCall(); // Trigger EMI calculation
            } else {
                setLoanAmountText(formatToIndianRupee(loanAmount)); // Reset to previous valid value if invalid
            }

            selection.removeAllRanges();
            selection.addRange(range);
        }, 500);
    };

    const handleInterestRateInput = (e) => {
        let value = e.target.innerText.trim(); // Get the entered value
        value = value.replace('%', '').trim(); // Remove % symbol and trim
        value = parseFloat(value); // Convert to Float
    
        if (!isNaN(value) && value >= loanFactorApiResponse.minROI && value <= loanFactorApiResponse.maxROI) {
            setInterestRate(value); // Update state if valid
            setInterestRateText(`${value}%`);
            debounceApiCall();
        } else {
            setInterestRateText(`${interestRate}%`); 
        }
    };

    const handleLoanTenureInput = (e) => {
        let value = e.target.innerText.trim(); // Get the entered value
        value = value.replace('months','').trim();
        value = parseInt(value); // Convert to number
        
    
        if (!isNaN(value) && value >= loanFactorApiResponse.minTenure && value <= loanFactorApiResponse.maxTenure) {
            setLoanTenure(value); 
            setLoanTenureText(`${value} months`)
            debounceApiCall(); // Trigger EMI calculation
        } else {
            setLoanTenureText(`${loanTenure} months`)
        }
    };

    const emi = apiResponse.emi || 0;  
    const totalPayment = apiResponse.totalPayment || 0;  
    const interestPayable = apiResponse.totalInterest || 0;  
    
    const data = {  
        labels: [t('doughnut-chart-label1'), t('doughnut-chart-label2')],  
        datasets: [  
            {  
                data: [interestPayable, loanAmount],  
                backgroundColor: ['#283782','#627DD4'],
                borderWidth:0,  
            },  
        ],      
    }; 
    
    const options = {
        plugins: {
            legend: {
                display: false, 
            },
            tooltip: {
                enabled: true, 
                position: 'nearest', 
                backgroundColor: 'rgba(0, 0, 0, 0.7)', 
                titleColor: '#ffffff', 
                bodyColor: '#ffffff', 
                borderColor: '#ffffff', 
                borderWidth: 1, 
                padding: 5,
                displayColors: false, 
                callbacks: {
                    label: function(tooltipItem) {
                        return `₹ ${tooltipItem.raw}`; 
                    },
                },
            },
        },
        cutout:'65%'
    };

    {/* Form Section */}


    
    const [formData, setFormData] = useState({
        productType: productType,
        loanAmount: "",
        mobile: "",
        name: "",
        businessName: "",
        isConsentAccepted:false
    });

    
    const setCustomer = useChatStore((state) => state.setCustomer); // ✅ Zustand function
    const setAuthToken = useChatStore((state) => state.setAuthToken);
    const navigate = useNavigate();

    // Update formData whenever loanAmount changes
    useEffect(() => {
        setFormData(prevData => ({
            ...prevData,
            loanAmount: loanAmount,
            productType: productType
        }));
    }, [loanAmount, productType]);

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: name === "mobile" ? parseInt(value, 10) ||name==="loanAmount"||"" : value,
        });
        if (name === "mobile") {
            setIsMobileValid(/^[6-9]\d{9}$/.test(value)); // Update validity based on regex
        }
    };
    const handleSubmit = async (e, isMobileForm = false) => {
        e.preventDefault();
        
        // If mobile is not valid, focus on the input and return
        if (!isMobileValid) {
            const inputId = isMobileForm ? "contact" : "myInput";
            const inputElement = document.getElementById(inputId);
            if (inputElement) {
                inputElement.focus();
            }
            return;
        }
        
        const messageDiv = isMobileForm ? document.getElementById("frm-error-mobile") : document.getElementById("frm-error");
        try {
            const submissionData = { 
                ...formData, 
                loanAmount: loanAmount,
                utmCampaign: utm.utmCampaign || "GrowBizh",
                utmSource: utm.utmSource || null,
                utmMedium: utm.utmMedium || null,
            };
            const response = await fetch(`${CREDIT_API_URL}create-lead`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(submissionData),
            });
            const result = await response.json();
            if (
                response.status === 200 ||
                response.status === 400 ||
                (response.status === 409 && (formData.productType === "Commercial Vehicle Loan" || formData.productType === "Business Loan"))
            ) {
                // Ensure mobile is included in leadData
                const enrichedLeadData = {
                    ...result.data,
                    mobile: result.data.mobile || formData.mobile
                };
                setLeadData(enrichedLeadData); // Store lead data for later use
                const otpResponse = await axios.post(`${CREDIT_API_URL}generate-otp`, {
                    mobile: formData.mobile,
                    productType: formData.productType,
                });
                if (otpResponse.status === 200) {
                    setShowOtpModal(true);
                } else {
                    throw new Error("OTP generation failed.");
                }
            } else {
                messageDiv.style.color = "red";
                messageDiv.textContent = result.message || "Something went wrong.";
                messageDiv.style.display = "block";
                setTimeout(() => messageDiv.style.display = "none", 3000);
            }
        } catch (error) {
            messageDiv.style.color = "red";
            messageDiv.textContent = error.message;
            messageDiv.style.display = "block";
            setTimeout(() => messageDiv.style.display = "none", 3000);
        }
    };

    const handleOtpSuccess = async () => {
        try {
            if (!leadData) throw new Error("Lead data missing. Please try again.");
            
            // Step 1: Call customer sign-in API to get dynamic token
            const mobile = leadData.mobile || formData.mobile;
            console.log("[DEBUG] Signing in customer with mobile:", mobile);
            
            const signInResult = await customerSignIn(mobile);
            
            // Step 2: Handle sign-in result
            if (signInResult.success && signInResult.token) {
                // Returning customer - has existing leads
                console.log("[DEBUG] Returning customer detected, redirecting to dashboard");
                setAuthToken(signInResult.token);
                setCustomer(signInResult.customer);
                showToast("Welcome back! Redirecting to your dashboard...", "success");
                navigate('/dashboard');
                return;
            } else if (signInResult.isNewCustomer) {
                // New customer - no existing leads (404 from API)
                console.log("[DEBUG] New customer detected, proceeding with chat flow");
                await setCustomer(leadData);
                useChatStore.setState({ chatSessionId: leadData.chatSessionId, loanId: leadData.loanRequestId });
                
                if (formData.productType === "Commercial Vehicle Loan" || formData.productType === "Business Loan") {
                    window.location.href = `/bizzie`;
                } else {
                    const msg = document.getElementById("frm-error");
                    msg.style.color = "green";
                    msg.textContent = "Thanks!! We will get back to you soon.";
                    msg.style.display = "block";
                }
            } else {
                // Sign-in failed for other reasons
                throw new Error(signInResult.message || "Failed to verify customer status");
            }
        } catch (err) {
            console.error("[ERROR] in handleOtpSuccess:", err);
            showToast(err.message || "Customer creation failed. Please try again.", "error");
        }
    };

    // Handle button click when disabled to focus on input
    const handleDisabledButtonClick = (isMobileForm = false) => {
        if (!isMobileValid) {
            const inputId = isMobileForm ? "contact" : "myInput";
            const inputElement = document.getElementById(inputId);
            if (inputElement) {
                inputElement.focus();
            }
        }
    };
    
 
    return (  
        <>
        <section className="section-container">  
            <div className="commercial-vehicle-emi-section">  
                <motion.div 
                    className="commercial-vehicle-emi-section-top"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.2, margin: "0px 0px -100px 0px" }}
                    variants={fadeInUp}
                >  
                    <motion.div 
                        className="commercial-vehicle-emi-section-top-content"
                        variants={fadeInUp}
                    >  
                        <motion.img 
                            src="/images/commercial-vehicle-emi.png" 
                            alt="logo"
                            variants={fadeInUp}
                        />  
                        {t('commercial-vehicle-emi-section-heading')}
                        <motion.p
                            variants={fadeInUp}
                            transition={{ delay: 0.2 }}
                        >
                            {t('commercial-vehicle-emi-section-description')}
                        </motion.p>  
                    </motion.div>  
                </motion.div>  
                <motion.div 
                    className="commercial-vehicle-emi-section-main"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.2, margin: "0px 0px -100px 0px" }}
                    variants={fadeInLeft}
                >
                <div className="commercial-vehicle-emi-section-bottom">  
                    <motion.div 
                        className="commercial-vehicle-emi-section-bottom-left"
                        variants={fadeInLeft}
                    >  
                        <div className="commercial-vehicle-emi-section-bottom-left-step">  
                          <div className="commercial-vehicle-emi-section-bottom-left-step-top">
                            <div className="commercial-vehicle-emi-section-bottom-left-step-top-label"><label htmlFor="loan-amount"><span>{t('left-commercial-vehicle-emi-section-step1.line2')}</span> </label></div>
                            <div className="commercial-vehicle-emi-section-bottom-left-step-top-value"
                            contentEditable
                            suppressContentEditableWarning
                            onInput={handleLoanAmountInput}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleLoanAmountInput(e);
                                }
                            }}
                            >
                                
                                {formatToIndianRupee(loanAmount)}</div>
                          </div>
                              <div className="commercial-vehicle-emi-section-bottom-left-step-bottom"> 
                              
                                <input   
                                type="range" 
                                className="commercial-vehicle-emi-section-bottom-left-step-bottom-input"    
                                id="loan-amount"   
                                min={loanFactorApiResponse.minLoanAmount}  
                                max={loanFactorApiResponse.maxLoanAmount}   
                                step="1000" 
                                value={loanAmount}   
                                onChange={handleLoanAmountChange}
                               
                            /> 
                            </div>
                            <div  className="range-slider-container">
                                <label htmlFor="loan-amount" className="range-label min-label">{formatToIndianRupee(loanFactorApiResponse.minLoanAmount)}</label>
                                <label htmlFor="loan-amount" className="range-label max-label">{formatToIndianRupee(loanFactorApiResponse.maxLoanAmount)}</label>
                            </div>
                           
                            
                        </div>  
                        <div className="commercial-vehicle-emi-section-bottom-left-step"> 
                        <div className="commercial-vehicle-emi-section-bottom-left-step-top">
                            <div className="commercial-vehicle-emi-section-bottom-left-step-top-label"><label htmlFor="loan-interest"><span>{t('left-commercial-vehicle-emi-section-step2.line2')}</span></label></div>
                            <div className="commercial-vehicle-emi-section-bottom-left-step-top-value"
                             contentEditable
                             suppressContentEditableWarning
                             onInput={handleInterestRateInput}
                             onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                e.preventDefault();
                                handleInterestRateInput(e);
                                }
                             }}
                            >{interestRate}%</div>
                          </div>
                              <div className="commercial-vehicle-emi-section-bottom-left-step-bottom"> 
                              <input   
                                type="range"   
                                id="loan-interest"   
                                min={loanFactorApiResponse.minROI}   
                                max={loanFactorApiResponse.maxROI}
                                step="0.25"
                                value={interestRate}   
                                onChange={handleInterestRateChange}  
                            /> 
                            </div> 
                            <div  className="range-slider-container">
                                <label htmlFor="loan-interest" className="range-label min-label">{loanFactorApiResponse.minROI} %</label>
                                <label htmlFor="loan-interest" className="range-label max-label">{loanFactorApiResponse.maxROI} %</label>
                            </div>    
                        </div>  
                        <div className="commercial-vehicle-emi-section-bottom-left-step">  
                        <div className="commercial-vehicle-emi-section-bottom-left-step-top">
                            <div className="commercial-vehicle-emi-section-bottom-left-step-top-label"><label htmlFor="loan-tenure"><span>{t('left-commercial-vehicle-emi-section-step3.line2')}</span></label></div>
                            <div className="commercial-vehicle-emi-section-bottom-left-step-top-value"
                                contentEditable
                                suppressContentEditableWarning
                                onInput={handleLoanTenureInput}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleLoanTenureInput(e);
                                    }
                                }}
                            >{loanTenure} {t('left-commercial-vehicle-emi-section-step3.line3')}</div>
                          </div>
                              <div className="commercial-vehicle-emi-section-bottom-left-step-bottom"> 
                              <input   
                                type="range"  
                                className="commercial-vehicle-emi-section-bottom-left-step-bottom-input" 
                                id="loan-tenure"   
                                min={loanFactorApiResponse.minTenure}   
                                max={loanFactorApiResponse.maxTenure}    
                                value={loanTenure}   
                                onChange={handleLoanTenureChange}   
                            /> 
                            </div> 
                            <div  className="range-slider-container">
                                <label htmlFor="loan-tenure" className="range-label min-label">{loanFactorApiResponse.minTenure} {t('minimum-loan-tenure')}</label>
                                <label htmlFor="loan-tenure" className="range-label max-label">{loanFactorApiResponse.maxTenure} {t('maximum-loan-tenure')}</label>
                            </div>  
                        </div>  
                    </motion.div>          
                    <motion.div 
                        className="commercial-vehicle-emi-section-bottom-right"
                        variants={fadeInRight}
                    >  
                        <div className="commercial-vehicle-emi-section-bottom-right-top">
                            <div className="commercial-vehicle-emi-section-bottom-right-top-title">{t('right-commercial-vehicle-emi-section-title')}</div> 
                            <div className="commercial-vehicle-emi-section-bottom-right-top-chart">
                                <img src="/images/rupee-emi-section.png" alt=""/>
                                <Doughnut 
                                data={data} 
                                options={options}
                                />
                            </div>  
                        </div>  
                        <div className="commercial-vehicle-emi-section-bottom-right-bottom">  
                            
                            <div className="commercial-vehicle-emi-section-bottom-right-bottom-content">
                                <div className="commercial-vehicle-emi-section-label">
                                    <div className="commercial-vehicle-emi-section-label-img"><img src="/images/light-blue-circle.png" alt=""/></div>
                                    <div className="commercial-vehicle-emi-section-label-title">{t('commercial-vehicle-emi-section-bottom-right.line1')}</div>
                                </div>
                                <div className="commercial-vehicle-emi-section-bottom-right-bottom-content-value">{formatToIndianRupee(loanAmount)}</div>
                            </div>
                                <div className="commercial-vehicle-emi-section-bottom-right-bottom-content">
                                <div className="commercial-vehicle-emi-section-label">
                                    <div className="commercial-vehicle-emi-section-label-img"><img src="/images/dark-blue-circle.png" alt=""/></div>
                                    <div className="commercial-vehicle-emi-section-label-title">{t('commercial-vehicle-emi-section-bottom-right.line2')}</div>
                                </div>
                                    <div className="commercial-vehicle-emi-section-bottom-right-bottom-content-value">{formatToIndianRupee(apiResponse.totalInterest)}</div>
                                </div> 
                            <div className="commercial-vehicle-emi-section-bottom-right-bottom-content">
                                <img src="/images/emi-section-line.png" alt=""/></div>   
                                
                            <div className="commercial-vehicle-emi-section-bottom-right-bottom-content">
                                <div className="commercial-vehicle-emi-section-bottom-right-bottom-content-title">{t('commercial-vehicle-emi-section-bottom-right.line3')}</div>
                                <div className="commercial-vehicle-emi-section-bottom-right-bottom-content-value"> {formatToIndianRupee(apiResponse.emi)}</div>
                            </div>
                        </div>  
                    </motion.div>  
                </div>  
            
            <motion.div 
                className='commercial-vehicle-form-section' 
                ref={ref}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2, margin: "0px 0px -100px 0px" }}
                variants={fadeInUp}
            >
                <motion.div 
                    className='commercial-vehicle-form-section-top'
                    variants={fadeInUp}
                >

                    <motion.div 
                        className='commercial-vehicle-form-section-top-description'
                        variants={fadeInUp}
                    >
                        {t('commercial-vehicle-form-section-description.line1')} {t('commercial-vehicle-form-section-description.line2')}
                    </motion.div>
                </motion.div>

                <motion.div 
                    className='commercial-vehicle-form-section-bottom'
                    variants={fadeInUp}
                >
                    <div className="commercial-vehicle-form">
                        <form className="form-horizontal commercial-vehicle-form-content" onSubmit={handleSubmit}>
                            <div style={{display:'flex', flexDirection:'column', width:'60%'}}> 

                            <div className="form-group commercial-vehicle-input-group">

                                <img src={`${ASSETS_URL}images/mobile.svg`} alt="" />
                                <input type="text" className={`form-control ${isMobileValid ? 'valid' : ''} input-field`} required placeholder="" name="mobile" value={formData.mobile}
                                    onChange={handleChange} id="myInput" autoComplete='off' />
                                <label htmlFor="myInput" className="emi-placeholder">{t('commercial-vehicle-form-placeholder')}</label>
                                {isMobileValid && <img src='/images/form-mobile-tick-icon-desktop.svg' alt='' className='valid-icon' />}
                            </div>
                            <div className="form-group consent-group" style={{marginTop:'2px',width:'110%'}}>
                                <input
                                type="checkbox"
                                id="consent"
                                name="isConsentAccepted"
                                checked={formData.isConsentAccepted}
                                onChange={e =>
                                    setFormData(prev => ({
                                    ...prev,
                                    isConsentAccepted: e.target.checked
                                }))
                                }
                                style={{marginLeft:'0px'}}
                                />
                                <label htmlFor="consent" style={{ margin: 0, cursor: 'pointer' }}>
                                    {t('form-description',{ns:'homepage'})}
                                </label>
                            </div>
                            </div>

                            <motion.div 
                                className="form-group commercial-vehicle-form-button"
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                <button 
                                    className={`btn-small-mobile btn-commercial-vehicle-form btn-primary${(!isMobileValid || !formData.isConsentAccepted) ? ' disabled' : ''}`}
                                    disabled={!isMobileValid || !formData.isConsentAccepted}
                                    onClick={(e) => {
                                        if (!isMobileValid || !formData.isConsentAccepted) {
                                            e.preventDefault();
                                            handleDisabledButtonClick(false);
                                        }
                                    }}
                                    type={isMobileValid && formData.isConsentAccepted ? "submit" : "button"}
                                >
                                    {t('commercial-vehicle-form-button')}
                                </button>
                            </motion.div>
                            <div><div id='frm-error'></div></div>
                        </form>
                    </div>

                    {/*Mobile */}
                    <div className="commercial-vehicle-form-mobile" ref={ref}>
                        <form className="form-horizontal commercial-vehicle-form-mobile-content" onSubmit={(e) => handleSubmit(e, true)}>

                            <div className="form-group commercial-vehicle-mobile-input-group">

                                <img src={`${ASSETS_URL}images/mobile.svg`} alt="" />
                                <input type="text" className={`form-control ${isMobileValid ? 'valid' : ''} input-field`} required placeholder="" name="mobile" value={formData.mobile}
                                    onChange={handleChange} autoComplete='off' id="contact"/>
                                    <label htmlFor="contact" className="placeholder">{t('commercial-vehicle-form-placeholder')}</label>
                                {isMobileValid && <img src='/images/form-mobile-tick-icon-desktop.svg' alt='' className='valid-icon' />}
                            </div>
                            <div className="form-group consent-group" style={{marginTop:'2px',width:'110%'}}>
                                <input
                                type="checkbox"
                                id="consent"
                                name="isConsentAccepted"
                                checked={formData.isConsentAccepted}
                                onChange={e =>
                                    setFormData(prev => ({
                                    ...prev,
                                    isConsentAccepted: e.target.checked
                                }))
                                }
                                style={{marginLeft:'0px'}}
                                />
                                <label htmlFor="consent" style={{ margin: 0, cursor: 'pointer' }}>
                                    {t('form-description',{ns:'homepage'})}
                                </label>
                            </div>
                            <div className="form-group" id='frm-error-mobile'></div>
                            <motion.div 
                                className="form-group commercial-vehicle-mobile-form-button mt-1 mb-1"
                                variants={buttonVariants}
                                whileHover="hover"
                                whileTap="tap"
                            >
                                <button 
                                    className={`btn-commercial-vehicle-mobile-form btn-primary${(!isMobileValid || !formData.isConsentAccepted) ? ' disabled' : ''}`}
                                    disabled={!isMobileValid || !formData.isConsentAccepted}
                                    onClick={(e) => {
                                        if (!isMobileValid || !formData.isConsentAccepted) {
                                            e.preventDefault();
                                            handleDisabledButtonClick(true);
                                        }
                                    }}
                                    type={isMobileValid && formData.isConsentAccepted ? "submit" : "button"}
                                >
                                    {t('commercial-vehicle-form-button')}
                                </button>
                            </motion.div>

                        </form>
                    </div>
                </motion.div>
            </motion.div>
            </motion.div>
            </div>
        </section>  
        <OtpVerificationModal
                show={showOtpModal}
                mobile={formData.mobile}
                productType={formData.productType}
                onClose={() => setShowOtpModal(false)}
                onVerified={handleOtpSuccess}
            />
   </>
    );  
})  

export default CommercialVehicleEmiSection;